import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, FileText, TrendingUp, Zap } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-6 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/bloodResults.png"
              alt="Blood Results Logo"
              className="h-8 w-auto"
            />
            <span className="text-lg font-semibold text-gray-900">
              Blood Results
            </span>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/features" className="text-gray-700 hover:text-gray-900">
              Features
            </Link>
            <Link to="/about" className="text-blue-600 font-semibold hover:text-blue-700">
              About
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-gray-900">
              Contact
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About <span className="text-blue-600">Blood Results</span>
            </h1>
            <p className="text-xl text-gray-600">
              Born from frustration, built with purpose
            </p>
          </div>

          {/* Story Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Problem</h2>

            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p>
                Like many people interested in optimizing their health, I get regular blood tests done.
                Multiple times per year, from different labs, tracking various biomarkers to see how diet,
                exercise, and lifestyle changes affect my health.
              </p>

              <p>
                But there was a fundamental problem: <strong>I couldn't easily see how my results changed over time</strong>.
              </p>

              <p>
                Each test came back as a PDF. Just a static document with numbers and reference ranges.
                Want to see if your vitamin D levels improved after supplementation? Want to track your cholesterol
                trend over the past year? Want to correlate testosterone levels with your training program?
              </p>

              <p className="text-xl font-semibold text-gray-900">
                You'd have to manually type every number into a spreadsheet.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 my-8">
                <p className="font-semibold text-red-900 mb-2">The Time Sink:</p>
                <ul className="space-y-2 text-red-800">
                  <li>• Open PDF, find test name, type value into spreadsheet</li>
                  <li>• Repeat for 30-50 different biomarkers</li>
                  <li>• Do this for every test (multiple per year)</li>
                  <li>• Manually create charts to visualise trends</li>
                  <li>• <strong>Hours of tedious data entry, every single time</strong></li>
                </ul>
              </div>

              <p>
                I'm a software engineer. I knew there had to be a better way. So I built one.
              </p>
            </div>
          </div>

          {/* Solution Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Solution</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Intelligent Parsing</h3>
                  <p className="text-gray-700">
                    Upload a PDF, get all your data extracted automatically in seconds
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Automatic Trend Tracking</h3>
                  <p className="text-gray-700">
                    Beautiful charts showing how your biomarkers change over time
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Multi-Lab Support</h3>
                  <p className="text-gray-700">
                    Works with results from different labs, all in one place
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Saves Hours</h3>
                  <p className="text-gray-700">
                    From hours of manual entry to seconds per test
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <p className="text-lg font-semibold text-gray-900 mb-3">
                What used to take hours now takes seconds.
              </p>
              <p className="text-gray-700">
                Upload your PDF, see all your trends instantly. No manual data entry.
                No spreadsheets. Just insights.
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>

            <p className="text-lg text-gray-700 mb-6">
              We believe that everyone should have easy access to their health data. Not buried in PDFs,
              not requiring hours of manual work, but visualised and actionable.
            </p>

            <p className="text-lg text-gray-700 mb-6">
              Whether you're an individual tracking your health optimisation journey, or a clinic
              wanting to provide better insights to your patients, Blood Results makes health data
              accessible.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6">
              <p className="text-blue-900 font-semibold">
                Your health data should work for you, not the other way around.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Take Control of Your Health Data?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join others who've stopped wasting time on manual data entry
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition duration-200 shadow-lg shadow-blue-500/30"
              >
                Get Started Free
              </Link>
              <Link
                to="/contact"
                className="inline-block bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-8 rounded-lg transition duration-200 border-2 border-gray-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-6 px-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; 2025 Blood-Results.co.uk A KNX-Associates Ltd Brand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
